import React from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import withStyles from 'isomorphic-style-loader/withStyles';
import Glyphicon from 'react-bootstrap/lib/Glyphicon';
import CurrencyConverter from '../CurrencyConverter';
import messages from '../../locale/jsonMessages';
import { getDiscountDays, getDiscountPresentation } from '../../helpers/discountPresentation';
import s from './ListingDiscounts.css';

// White scalloped seal with a green percent sign, as in the badge mockup.
const SEAL_PATH = 'M23.60 12.00 L23.46 12.60 L23.09 13.17 L22.57 13.67 L22.03 14.13 L21.58 14.57 L21.32 15.03 L21.26 15.56 L21.36 16.17 L21.53 16.86 L21.66 17.57 L21.63 18.25 L21.38 18.82 L20.92 19.22 L20.29 19.46 L19.57 19.57 L18.86 19.62 L18.24 19.71 L17.76 19.93 L17.40 20.32 L17.12 20.88 L16.86 21.53 L16.54 22.19 L16.11 22.72 L15.58 23.03 L14.97 23.09 L14.32 22.91 L13.67 22.57 L13.07 22.19 L12.52 21.91 L12.00 21.80 L11.48 21.91 L10.93 22.19 L10.33 22.57 L9.68 22.91 L9.03 23.09 L8.42 23.03 L7.89 22.72 L7.46 22.19 L7.14 21.53 L6.88 20.88 L6.60 20.32 L6.24 19.93 L5.76 19.71 L5.14 19.62 L4.43 19.57 L3.71 19.46 L3.08 19.22 L2.62 18.82 L2.37 18.25 L2.34 17.57 L2.47 16.86 L2.64 16.17 L2.74 15.56 L2.68 15.03 L2.42 14.57 L1.97 14.13 L1.43 13.67 L0.91 13.17 L0.54 12.60 L0.40 12.00 L0.54 11.40 L0.91 10.83 L1.43 10.33 L1.97 9.87 L2.42 9.43 L2.68 8.97 L2.74 8.44 L2.64 7.83 L2.47 7.14 L2.34 6.43 L2.37 5.75 L2.62 5.18 L3.08 4.78 L3.71 4.54 L4.43 4.43 L5.14 4.38 L5.76 4.29 L6.24 4.07 L6.60 3.68 L6.87 3.12 L7.14 2.47 L7.46 1.81 L7.89 1.28 L8.42 0.97 L9.03 0.91 L9.68 1.09 L10.33 1.43 L10.93 1.81 L11.48 2.09 L12.00 2.20 L12.52 2.09 L13.07 1.81 L13.67 1.43 L14.32 1.09 L14.97 0.91 L15.58 0.97 L16.11 1.28 L16.54 1.81 L16.86 2.47 L17.12 3.12 L17.40 3.68 L17.76 4.07 L18.24 4.29 L18.86 4.38 L19.57 4.43 L20.29 4.54 L20.92 4.78 L21.38 5.18 L21.63 5.75 L21.66 6.43 L21.53 7.14 L21.36 7.83 L21.26 8.44 L21.32 8.97 L21.58 9.43 L22.03 9.87 L22.57 10.33 L23.09 10.83 L23.46 11.40Z';
function DiscountSeal({ size = 24, seal = '#fff', mark = '#087f50' }) {
    return <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d={SEAL_PATH} fill={seal} />
        <text x="12" y="16.4" textAnchor="middle" fontSize="13" fontWeight="700" fontFamily="Arial, sans-serif" fill={mark}>%</text>
    </svg>;
}

function ListingDiscounts({ weeklyDiscount, monthlyDiscount, startDate, endDate, startTime, endTime,
    variant = 'offers', additionalDays, additionalSavings, projectedSavings, currency, onExtend, intl }) {
    const days = getDiscountDays({ startDate, endDate, startTime, endTime });
    const { offers, featured, next } = getDiscountPresentation({ weeklyDiscount, monthlyDiscount }, days);
    if (!featured) return null;
    const label = offer => intl.formatMessage(messages.discountFromDays, {
        percentage: intl.formatNumber(offer.percentage), days: offer.days,
    });

    if (variant === 'badge') {
        // Teaser only: always the biggest discount, whatever the dates, to push the click to the details.
        const { featured: biggest } = getDiscountPresentation({ weeklyDiscount, monthlyDiscount });
        return <span className={s.badge}><span className={s.badgeIcon}><DiscountSeal size={26} /></span>
            {intl.formatMessage(messages.discountUpTo, { percentage: intl.formatNumber(biggest.percentage) })}</span>;
    }
    if (variant === 'next') {
        if (!next) return null;
        const extraDays = additionalDays || next.days - days;
        const savings = Number.isFinite(additionalSavings) && additionalSavings > 0
            ? { key: messages.discountExtendSavings, amount: additionalSavings }
            : Number.isFinite(projectedSavings) && projectedSavings > 0
                ? { key: messages.discountExtendSavingsTotal, amount: projectedSavings } : null;
        return <div className={`${s.panel} ${s.nextPanel}`} aria-live="polite">
            <span className={s.nextIcon} aria-hidden="true"><Glyphicon glyph="flash" /></span>
            <div className={s.nextContent}>
                <strong>{savings
                    ? <FormattedMessage {...savings.key} values={{ days: extraDays, totalDays: next.days,
                        amount: <CurrencyConverter amount={savings.amount} from={currency} /> }} />
                    : intl.formatMessage(messages.discountDaysRemaining, { days: extraDays, percentage: intl.formatNumber(next.percentage) })}</strong>
                {onExtend && <button type="button" className={s.extendButton} onClick={onExtend}>
                    <Glyphicon glyph="plus" aria-hidden="true" />{' '}
                    {intl.formatMessage(messages.discountExtendButton, { days: extraDays })}
                </button>}
            </div>
        </div>;
    }
    const [titleTop, ...titleRest] = intl.formatMessage(messages.discountLongStayTitle).split('\n');
    const titleMain = titleRest.join(' ');
    const tierName = offer => offer.days >= 28 ? messages.discountMonth : messages.discountWeek;
    if (variant === 'summary') {
        return <div className={s.summary}>
            <span className={s.summaryIcon} aria-hidden="true">%</span>
            <ul className={s.summaryOffers}>{offers.map(offer => <li key={offer.days}>{label(offer)}</li>)}</ul>
        </div>;
    }
    return <section className={s.offersCard}>
        <div className={s.offersRow}>
            <div className={s.offersHeader}>
                <span className={s.offersSeal}><DiscountSeal size={36} seal="#16794f" mark="#fff" /></span>
                <div className={s.offersTexts}>
                    {titleMain ? <span className={s.titleTop}>{titleTop}</span> : null}
                    <strong>{titleMain || titleTop}</strong>
                    <small>{intl.formatMessage(messages.discountLongStaySubtitle)}</small>
                </div>
            </div>
            <ul className={s.tiles}>{offers.map(offer => <li key={offer.days} className={s.tile}>
                <span className={s.tileName}><Glyphicon glyph="calendar" aria-hidden="true" /> {intl.formatMessage(tierName(offer))}</span>
                <span className={s.tilePercent}>{`−${intl.formatNumber(offer.percentage)} %`}</span>
                <span className={s.tileDays}>{intl.formatMessage(messages.discountFromDaysShort, { days: offer.days })}</span>
            </li>)}</ul>
        </div>
        <details className={s.conditions}>
            <summary>{intl.formatMessage(messages.discountOfferConditions)}</summary>
            <small>{intl.formatMessage(messages.discountNotCumulative)}</small>
        </details>
    </section>;
}

export default injectIntl(withStyles(s)(ListingDiscounts));
